import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useNavigate, useLocation } from 'react-router-dom';
import AddIcon from '@mui/icons-material/AddCircle';
import SearchIcon from '@mui/icons-material/Search';
import LocationIcon from '@mui/icons-material/LocationOn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import UsersIcon from '@mui/icons-material/Group';
import { getTutorialSteps } from '../../services/firebaseData';

const FALLBACK_TUTORIAL_STEPS = [
  {
    icon: 'add',
    title: 'Crea il tuo tavolo',
    description: 'Scegli tema, luogo e orario per proporre un incontro conviviale.',
  },
  {
    icon: 'search',
    title: 'Trova tavoli vicini',
    description: 'Esplora la mappa o la lista per scoprire occasioni interessanti.',
  },
  {
    icon: 'users',
    title: 'Partecipa',
    description: 'Unisciti ai tavoli disponibili e incontra nuove persone.',
  },
  {
    icon: 'notification',
    title: 'Resta aggiornato',
    description: 'Ricevi conferme e aggiornamenti sui tavoli a cui partecipi.',
  },
];

export default function TutorialPage() {
  const navigate = useNavigate();
  const { currentUser, tutorialCompleted, completeTutorial } = useAuth();
  const userId = currentUser?.uid;
  const location = useLocation();
  const replay = location.state?.replay === true;
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [steps, setSteps] = useState([]); // Store tutorial steps
  const visibleSteps = useMemo(
    () => (steps.length > 0 ? steps : FALLBACK_TUTORIAL_STEPS),
    [steps],
  );

  const handleNext = () => {
    if (activeStep < visibleSteps.length - 1) {
      setActiveStep(activeStep + 1);
    } else {
      handleClosing();
    }
  };

  const handleClosing = async () => {
    setSaving(true);
    setError('');
    try {
      await completeTutorial();
      navigate('/index', { replace: true });
    } catch {
      setError('Impossibile salvare il tutorial. Riprova per continuare.');
    } finally {
      setSaving(false);
    }
  };

  // Fetch tutorial steps from the server
  useEffect(() => {
    if (!userId) {
      navigate('/', { replace: true });
      return;
    }
    if (tutorialCompleted && !replay) {
      navigate('/index', { replace: true });
      return;
    }

    const fetchTutorialSteps = async () => {
      try {
        const data = await getTutorialSteps();
        setSteps(data);
      } catch (error) {
        console.error('Error fetching tutorial steps:', error);
      }
    };

    fetchTutorialSteps();
  }, [userId, tutorialCompleted, replay, navigate]);

  if (!userId || (tutorialCompleted && !replay)) return null;

  return (
    <Box
      sx={{
        padding: 3,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 500, padding: 3, boxShadow: 3 }}>
        <CardContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {visibleSteps[activeStep]?.icon === 'add' && (
              <AddIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            )}
            {visibleSteps[activeStep]?.icon === 'search' && (
              <SearchIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            )}
            {visibleSteps[activeStep]?.icon === 'location' && (
              <LocationIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            )}
            {visibleSteps[activeStep]?.icon === 'notification' && (
              <NotificationsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            )}
            {visibleSteps[activeStep]?.icon === 'users' && (
              <UsersIcon sx={{ fontSize: 40, color: 'primary.main' }} />
            )}
          </Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 'bold', textAlign: 'center', mt: 3 }}
          >
            {visibleSteps[activeStep]?.title}
          </Typography>
          <Typography
            variant="body1"
            sx={{ marginTop: 2, textAlign: 'center' }}
          >
            {visibleSteps[activeStep]?.description}
          </Typography>
          <Box
            sx={{
              marginTop: 3,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={handleClosing} color="secondary" disabled={saving}>
              Salta
            </Button>
            <Button onClick={handleNext} variant="contained" color="primary" disabled={saving}>
              {activeStep === visibleSteps.length - 1 ? 'Inizia ora' : 'Avanti'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Stepper on Top of the Card */}
      <Stepper
        activeStep={activeStep}
        orientation="vertical"
        sx={{ mt: 3, width: '100%', maxWidth: 500 }}
      >
        {visibleSteps.map((step, index) => (
          <Step key={index}>
            <StepLabel>{step.title}</StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}
